import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import classNames from 'classnames';
import React from 'react';
import { ComponentProviderList } from '$/(cms)/_modules/cmsService/types';
import Button from '$/components/Button';
import ArrowRightAlt from '$/components/icons/ArrowRightAlt';

const MOBILE_PATTERNS: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [3],
  4: [2, 2],
  5: [3, 2],
  6: [3, 3],
  7: [3, 2, 2],
  8: [3, 3, 2],
  9: [3, 3, 3],
  10: [3, 3, 2, 2],
};

const TABLET_PATTERNS: Record<number, number[]> = {
  1: [1],
  2: [2],
  3: [3],
  4: [4],
  5: [5],
  6: [5, 1],
};

function getPattern(
  patterns: Record<number, number[]>,
  count: number,
): number[] {
  const pattern = patterns[count];
  if (pattern) {
    return pattern;
  }
  if (count <= 0) {
    return [];
  }
  return [];
}

type GridRowProps = {
  cols?: number;
  auto?: boolean;
  children: React.ReactNode;
};

const GridRow: React.FC<GridRowProps> = ({ cols, auto = false, children }) => {
  const template = auto
    ? 'repeat(auto-fit, minmax(104px, 140px))'
    : cols
      ? `repeat(${cols}, minmax(104px, 1fr))`
      : 'repeat(1, minmax(104px, 1fr))';

  return (
    <div
      className="grid justify-start gap-spacing/gap/soft-12px"
      style={{ gridTemplateColumns: template }}
    >
      {children}
    </div>
  );
};

type NormalizedProvider = {
  id: number | string;
  altText: string;
  imageUrl: string;
  linkUrl?: string;
};

type ProvidersGridProps = {
  providersItems: ComponentProviderList['providerItem'];
  rowsPattern?: number[];
  auto?: boolean;
  wrapperClassName: string;
  imageClassName: string;
  /** @deprecated TODO: Remove after updating Strapi fields */
  providers: ComponentProviderList['providers'];
};

const ProvidersGrid: React.FC<ProvidersGridProps> = ({
  providersItems,
  providers = [],
  rowsPattern,
  auto = false,
  wrapperClassName,
  imageClassName,
}) => {
  const normalizedProviders: NormalizedProvider[] = providersItems?.length
    ? providersItems.map(({ id, altText, optionalLinkUrl, provider }) => ({
        id,
        altText,
        imageUrl: provider?.image.url ?? '',
        linkUrl: optionalLinkUrl,
      }))
    : providers.map(({ image, provider_name, documentId }, index) => ({
        id: documentId ?? index,
        altText: provider_name ?? 'Provider',
        imageUrl: image?.url ?? '',
      }));

  const renderProviderLogo = ({
    id,
    altText,
    imageUrl,
    linkUrl,
  }: NormalizedProvider) => {
    if (!imageUrl) return null;

    return (
      <div
        key={id}
        className="flex h-15 w-full items-center justify-center rounded-radius/button border border-borders/default p-spacing/gap/soft-12px bg-fill/card lg:max-w-35"
      >
        {linkUrl ? (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer">
            <CmsImage
              sizes={{ _: 150 }}
              src={imageUrl ?? ''}
              alt={altText ?? 'Provider'}
              width={80}
              height={36}
              className={imageClassName}
            />
          </a>
        ) : (
          <CmsImage
            sizes={{ _: 150 }}
            src={imageUrl ?? ''}
            alt={altText ?? 'Provider'}
            width={80}
            height={36}
            className={imageClassName}
          />
        )}
      </div>
    );
  };

  const renderProvidersList = (items: NormalizedProvider[]) => (
    <>{items.map((item) => renderProviderLogo(item))}</>
  );

  if (auto) {
    return (
      <div className={wrapperClassName}>
        <GridRow auto>{renderProvidersList(normalizedProviders)}</GridRow>
      </div>
    );
  }

  let offset = 0;

  return (
    <div className={wrapperClassName}>
      {rowsPattern?.map((cols, rowIndex) => {
        const rowItems = normalizedProviders.slice(offset, offset + cols);
        offset += cols;
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${cols}-${rowIndex}`}>
            <GridRow cols={cols}>{renderProvidersList(rowItems)}</GridRow>
          </div>
        );
      })}
    </div>
  );
};

export const ProviderList: React.FC<ComponentProviderList> = (props) => {
  const {
    title,
    subtitleAbove,
    subtitleBelow,
    providers = [],
    buttonText,
    callToAction,
    providerItem,
    displayCta,
    urlTarget,
  } = props;

  // TODO: Remove after updating Strapi fields
  const providersSource = providerItem?.length ? providerItem : providers;
  const providersCount = providersSource.length ?? 0;

  const ctaLink = callToAction?.links?.[0]?.redirected_url;

  const mobileRows = getPattern(MOBILE_PATTERNS, providersCount);
  const tabletRows = getPattern(TABLET_PATTERNS, providersCount);

  const providerImageClasses = classNames(
    'max-h-15 object-contain md:max-w-34 lg:max-w-35',
  );

  return (
    <div className="flex justify-start px-padding/section-x py-padding/section-y bg-fill/primary-light lg:justify-center">
      <div className="w-full lg:w-general/narrow-content-max-width-3 lg:max-w-general/narrow-content-max-width-3">
        <div className="flex flex-col gap-spacing/gap/y lg:max-w-general/narrow-content-max-width-1">
          {title && (
            <h2 className="mhg-alpha-typography-display-1 text-text/primary">
              {title}
            </h2>
          )}

          {subtitleAbove && (
            <div className="mhg-alpha-typography-body-1-relaxed text-text/secondary">
              {subtitleAbove}
            </div>
          )}

          {/* Mobile */}
          <ProvidersGrid
            providers={providers}
            providersItems={providerItem}
            rowsPattern={mobileRows}
            wrapperClassName="flex flex-col gap-spacing/gap/soft-12px md:hidden"
            imageClassName={providerImageClasses}
          />

          {/* Tablet */}
          <ProvidersGrid
            providers={providers}
            providersItems={providerItem}
            rowsPattern={tabletRows}
            auto={tabletRows.length === 0}
            wrapperClassName="hidden md:flex md:flex-col md:gap-spacing/gap/soft-12px lg:hidden"
            imageClassName={providerImageClasses}
          />

          {/* Desktop */}
          <ProvidersGrid
            providers={providers}
            providersItems={providerItem}
            auto
            wrapperClassName="hidden lg:block"
            imageClassName={providerImageClasses}
          />

          {subtitleBelow && (
            <div className="mhg-alpha-typography-body-1-relaxed">
              {subtitleBelow}
            </div>
          )}

          {buttonText && ctaLink && displayCta && (
            <a
              href={ctaLink}
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="w-full md:w-[275px]"
              target={urlTarget === 'BLANK' ? '_blank' : '_self'}
              rel="noreferrer noopener"
            >
              <Button
                className="w-full"
                endIcon={<ArrowRightAlt />}
                styleType="primary"
              >
                {buttonText}
              </Button>
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
